import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Tabs, 
  Tab, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip, 
  Avatar, 
  LinearProgress,
  Button,
  IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  Star as StarIcon,
  DateRange as DateRangeIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '4px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  }
}));

const MetricCard = styled(Card)(({ gradient }) => ({
  background: gradient,
  color: 'white',
  borderRadius: '4px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.3s ease',
  border: 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
  }
}));

const AdminAnalytics = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [timeRange, setTimeRange] = useState('30days');

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Mock analytics data
  const kpiData = {
    totalRevenue: 4556800,
    revenueGrowth: 12.5,
    totalOrders: 3560,
    ordersGrowth: 8.3,
    totalUsers: 12470,
    usersGrowth: 15.2,
    totalStores: 148,
    storesGrowth: 22.1,
    averageOrderValue: 1280,
    aovGrowth: -2.3,
    conversionRate: 3.4,
    conversionGrowth: 5.8
  };

  const revenueData = [
    { month: 'জানুয়ারি', revenue: 280000, orders: 245 },
    { month: 'ফেব্রুয়ারি', revenue: 320000, orders: 290 },
    { month: 'মার্চ', revenue: 380000, orders: 340 },
    { month: 'এপ্রিল', revenue: 420000, orders: 385 },
    { month: 'মে', revenue: 450000, orders: 410 },
    { month: 'জুন', revenue: 480000, orders: 445 },
  ];

  const topStores = [
    { id: 1, name: 'তাজা সবজি দোকান', owner: 'করিম উদ্দিন', revenue: 125000, orders: 156, growth: 18.5, rating: 4.8 },
    { id: 2, name: 'মাংস প্যারাডাইজ', owner: 'রহিম আলী', revenue: 189000, orders: 142, growth: 24.2, rating: 4.9 },
    { id: 3, name: 'ফলের রাজ্য', owner: 'সালেহা খাতুন', revenue: 98000, orders: 135, growth: 12.8, rating: 4.7 },
    { id: 4, name: 'মিষ্টি কর্নার', owner: 'আলিম মিয়া', revenue: 76000, orders: 98, growth: -5.2, rating: 4.6 },
    { id: 5, name: 'দুগ্ধ ভান্ডার', owner: 'ফরিদা বেগম', revenue: 67000, orders: 89, growth: 8.9, rating: 4.5 },
  ];

  const topProducts = [
    { id: 1, name: 'জৈব টমেটো', category: 'সবজি', sold: 1200, revenue: 144000, growth: 15.3 },
    { id: 2, name: 'দেশি মুরগি', category: 'মাংস', sold: 850, revenue: 425000, growth: 22.1 },
    { id: 3, name: 'কাঁচা আম', category: 'ফল', sold: 950, revenue: 190000, growth: 18.7 },
    { id: 4, name: 'তাজা দুধ', category: 'দুগ্ধ', sold: 2100, revenue: 126000, growth: 8.9 },
    { id: 5, name: 'দেশি চাল', category: 'খাদ্যশস্য', sold: 1800, revenue: 162000, growth: 12.4 },
  ];

  const userGrowthData = [
    { period: 'জানুয়ারি', newUsers: 145, totalUsers: 8230 },
    { period: 'ফেব্রুয়ারি', newUsers: 189, totalUsers: 8419 },
    { period: 'মার্চ', newUsers: 234, totalUsers: 8653 },
    { period: 'এপ্রিল', newUsers: 198, totalUsers: 8851 },
    { period: 'মে', newUsers: 267, totalUsers: 9118 },
    { period: 'জুন', newUsers: 312, totalUsers: 9430 },
  ];

  const geographicData = [
    { district: 'ঢাকা', orders: 1450, revenue: 1850000, percentage: 40.6 },
    { district: 'চট্টগ্রাম', orders: 890, revenue: 1120000, percentage: 24.6 },
    { district: 'সিলেট', orders: 560, revenue: 672000, percentage: 14.8 },
    { district: 'রাজশাহী', orders: 420, revenue: 504000, percentage: 11.1 },
    { district: 'খুলনা', orders: 240, revenue: 410800, percentage: 8.9 },
  ];

  const tabs = [
    { label: 'সামগ্রিক ওভারভিউ', icon: <TrendingUpIcon /> },
    { label: 'বিক্রয় বিশ্লেষণ', icon: <AttachMoneyIcon /> },
    { label: 'ব্যবহারকারী বিশ্লেষণ', icon: <PeopleIcon /> },
    { label: 'দোকান পারফরম্যান্স', icon: <StoreIcon /> },
    { label: 'পণ্য বিশ্লেষণ', icon: <InventoryIcon /> },
    { label: 'ভৌগোলিক বিশ্লেষণ', icon: <LocalShippingIcon /> },
  ];

  const formatCurrency = (amount) => `৳${amount.toLocaleString()}`;
  const formatPercentage = (value, showSign = true) => `${showSign && value > 0 ? '+' : ''}${value}%`;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          বিশ্লেষণ ও রিপোর্ট
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>সময়কাল</InputLabel>
            <Select
              value={timeRange}
              label="সময়কাল"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="7days">গত ৭ দিন</MenuItem>
              <MenuItem value="30days">গত ৩০ দিন</MenuItem>
              <MenuItem value="90days">গত ৯০ দিন</MenuItem>
              <MenuItem value="1year">গত ১ বছর</MenuItem>
            </Select>
          </FormControl>
          
          <IconButton color="primary">
            <RefreshIcon />
          </IconButton>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: '4px' }}
          >
            রিপোর্ট ডাউনলোড
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab 
                key={index}
                label={tab.label} 
                icon={tab.icon}
                iconPosition="start"
                sx={{ minWidth: 180 }}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Overview Tab */}
          {currentTab === 0 && (
            <Box>
              {/* KPI Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MetricCard gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <AttachMoneyIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {kpiData.revenueGrowth > 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                          <Typography variant="body2" sx={{ ml: 0.5 }}>
                            {formatPercentage(kpiData.revenueGrowth)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {formatCurrency(kpiData.totalRevenue)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        মোট রাজস্ব
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MetricCard gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <ShoppingCartIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ArrowUpwardIcon />
                          <Typography variant="body2" sx={{ ml: 0.5 }}>
                            {formatPercentage(kpiData.ordersGrowth)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {kpiData.totalOrders.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        মোট অর্ডার
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MetricCard gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ArrowUpwardIcon />
                          <Typography variant="body2" sx={{ ml: 0.5 }}>
                            {formatPercentage(kpiData.usersGrowth)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {kpiData.totalUsers.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        মোট ব্যবহারকারী
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MetricCard gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)">
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <StoreIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ArrowUpwardIcon />
                          <Typography variant="body2" sx={{ ml: 0.5 }}>
                            {formatPercentage(kpiData.storesGrowth)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {kpiData.totalStores}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        মোট দোকান
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MetricCard gradient="linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)">
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8, color: '#333' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {kpiData.aovGrowth > 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                          <Typography variant="body2" sx={{ ml: 0.5, color: '#333' }}>
                            {formatPercentage(kpiData.aovGrowth)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                        {formatCurrency(kpiData.averageOrderValue)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, color: '#333' }}>
                        গড় অর্ডার মূল্য
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <MetricCard gradient="linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)">
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <StarIcon sx={{ fontSize: 40, opacity: 0.8, color: '#333' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ArrowUpwardIcon />
                          <Typography variant="body2" sx={{ ml: 0.5, color: '#333' }}>
                            {formatPercentage(kpiData.conversionGrowth)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                        {kpiData.conversionRate}%
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, color: '#333' }}>
                        কনভার্শন রেট
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
              </Grid>

              {/* Charts Section */}
              <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        মাসিক রাজস্ব ট্রেন্ড
                      </Typography>
                      <Box sx={{ height: 300, display: 'flex', alignItems: 'end', gap: 2, mt: 3 }}>
                        {revenueData.map((item, index) => (
                          <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: '100%',
                                height: `${(item.revenue / 500000) * 250}px`,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '4px 4px 0 0',
                                mb: 1,
                                display: 'flex',
                                alignItems: 'end',
                                justifyContent: 'center',
                                pb: 1,
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}
                            >
                              {formatCurrency(item.revenue / 1000)}K
                            </Box>
                            <Typography variant="caption" sx={{ textAlign: 'center' }}>
                              {item.month}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        শীর্ষ ৫ দোকান
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {topStores.slice(0, 5).map((store, index) => (
                          <Box key={store.id} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: '#f8f9fa', borderRadius: '4px' }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              {index + 1}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {store.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(store.revenue)}
                              </Typography>
                            </Box>
                            <Chip
                              label={formatPercentage(store.growth)}
                              color={store.growth > 0 ? 'success' : 'error'}
                              size="small"
                            />
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Sales Analysis Tab */}
          {currentTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      দৈনিক বিক্রয় ট্রেন্ড
                    </Typography>
                    <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8f9fa', borderRadius: '4px' }}>
                      <Typography color="text.secondary">
                        চার্ট লোড হচ্ছে...
                      </Typography>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} lg={6}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      পণ্য ক্যাটাগরি অনুযায়ী বিক্রয়
                    </Typography>
                    <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8f9fa', borderRadius: '4px' }}>
                      <Typography color="text.secondary">
                        পাই চার্ট লোড হচ্ছে...
                      </Typography>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      মাসিক বিক্রয় রিপোর্ট
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>মাস</TableCell>
                            <TableCell align="right">রাজস্ব</TableCell>
                            <TableCell align="right">অর্ডার</TableCell>
                            <TableCell align="right">গড় অর্ডার মূল্য</TableCell>
                            <TableCell align="right">বৃদ্ধি</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {revenueData.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell>{row.month}</TableCell>
                              <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                              <TableCell align="right">{row.orders}</TableCell>
                              <TableCell align="right">{formatCurrency(Math.round(row.revenue / row.orders))}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`+${Math.round(Math.random() * 20)}%`}
                                  color="success"
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* User Analysis Tab */}
          {currentTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      ব্যবহারকারী বৃদ্ধির ট্রেন্ড
                    </Typography>
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'end', gap: 2, mt: 3 }}>
                      {userGrowthData.map((item, index) => (
                        <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: '100%',
                              height: `${(item.newUsers / 350) * 250}px`,
                              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                              borderRadius: '4px 4px 0 0',
                              mb: 1,
                              display: 'flex',
                              alignItems: 'end',
                              justifyContent: 'center',
                              pb: 1,
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}
                          >
                            +{item.newUsers}
                          </Box>
                          <Typography variant="caption" sx={{ textAlign: 'center' }}>
                            {item.period}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} lg={4}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      ব্যবহারকারীর ধরন
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">কাস্টমার</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>85%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={85} sx={{ mb: 3, height: 8, borderRadius: 4 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">দোকান মালিক</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>12%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={12} sx={{ mb: 3, height: 8, borderRadius: 4 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">অ্যাডমিন</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>3%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={3} sx={{ mb: 3, height: 8, borderRadius: 4 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">সাপোর্ট স্টাফ</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>2%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={2} sx={{ mb: 3, height: 8, borderRadius: 4 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">ইমপ্লয়ি</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>0.5%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={0.5} sx={{ mb: 3, height: 8, borderRadius: 4 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">উপজেলা এ্যাডমিন</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>1%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={1} sx={{ height: 8, borderRadius: 4 }} />
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Store Performance Tab */}
          {currentTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      দোকান পারফরম্যান্স র‍্যাঙ্কিং
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>র‍্যাঙ্ক</TableCell>
                            <TableCell>দোকানের নাম</TableCell>
                            <TableCell>মালিক</TableCell>
                            <TableCell align="right">রাজস্ব</TableCell>
                            <TableCell align="right">অর্ডার</TableCell>
                            <TableCell align="right">বৃদ্ধি</TableCell>
                            <TableCell align="right">রেটিং</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topStores.map((store, index) => (
                            <TableRow key={store.id}>
                              <TableCell>
                                <Avatar sx={{ bgcolor: index < 3 ? 'primary.main' : 'grey.400', width: 32, height: 32 }}>
                                  {index + 1}
                                </Avatar>
                              </TableCell>
                              <TableCell>{store.name}</TableCell>
                              <TableCell>{store.owner}</TableCell>
                              <TableCell align="right">{formatCurrency(store.revenue)}</TableCell>
                              <TableCell align="right">{store.orders}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={formatPercentage(store.growth)}
                                  color={store.growth > 0 ? 'success' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                  <StarIcon sx={{ color: '#ffc107', mr: 0.5, fontSize: 16 }} />
                                  {store.rating}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Product Analysis Tab */}
          {currentTab === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      শীর্ষ বিক্রিত পণ্যসমূহ
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>পণ্যের নাম</TableCell>
                            <TableCell>ক্যাটাগরি</TableCell>
                            <TableCell align="right">বিক্রিত সংখ্যা</TableCell>
                            <TableCell align="right">রাজস্ব</TableCell>
                            <TableCell align="right">বৃদ্ধি</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topProducts.map((product) => (
                            <TableRow key={product.id}>
                              <TableCell>{product.name}</TableCell>
                              <TableCell>
                                <Chip label={product.category} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="right">{product.sold.toLocaleString()}</TableCell>
                              <TableCell align="right">{formatCurrency(product.revenue)}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={formatPercentage(product.growth)}
                                  color="success"
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Geographic Analysis Tab */}
          {currentTab === 5 && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      জেলাভিত্তিক অর্ডার বিতরণ
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                      {geographicData.map((district, index) => (
                        <Box key={index} sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {district.district}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {district.percentage}% ({district.orders} অর্ডার)
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={district.percentage} 
                            sx={{ height: 10, borderRadius: 5 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            রাজস্ব: {formatCurrency(district.revenue)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} lg={4}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      মোট জেলাভিত্তিক পরিসংখ্যান
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                          64
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          সক্রিয় জেলা
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h4" color="secondary" sx={{ fontWeight: 700 }}>
                          148
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          সক্রিয় উপজেলা
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                          ৯৮%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ডেলিভারি কভারেজ
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminAnalytics; 